import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  RegistryFilterOption,
  SchemaInterfaceExtendingSchema,
  TagSchema,
  TagSchemaWithInterfaces,
} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {RequestSchema} from 'requests/RequestConstants';
import {ASSAY_TYPES, AssayRunSchema, ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {Schema} from 'schemas/SchemaConstants';
import APIUtil from 'util/APIUtil';

import {StorableSchemaGraphQL} from '../../graphql-fragments/inventory/schemas/__generated/StorableSchemaGraphQL';

type PlateSchemaGraphQL = ExtractByTypename<StorableSchemaGraphQL, 'PlateSchemaNode'>;

type BoxSchemaGraphQL = ExtractByTypename<StorableSchemaGraphQL, 'BoxSchemaNode'>;

// These functions return a
// [Typescript type predicate](https://www.typescriptlang.org/docs/handbook/advanced-types.html#using-type-predicates)
// that allow us to safely narrow the type bounds for a Schema.  For
// instance, instead of having to use a cast, Typescript can infer
// exactly what is needed like so:
//
//  const schema: Schema = getSomeSchemaFromSomewhere();
//  if (isTagSchema(schema)) {
//    return schema.name;
//  } else {
//    return schema.id;
//  }

export function isTagSchemaWithInterfaces(schema: Schema): schema is TagSchemaWithInterfaces {
  const tagSchemaWithInterfaces = schema as TagSchemaWithInterfaces;
  return isTagSchema(schema) && tagSchemaWithInterfaces.interfaces !== undefined;
}

export function isTagSchema(schema: Schema | BatchSchema | RegistryFilterOption): schema is TagSchema {
  const tagSchema = schema as TagSchema;
  return tagSchema.id != null && APIUtil.apiIdToMaybeType(tagSchema.id) === ModelTypeConstants.TAG_SCHEMA;
}

export function isSchemaInterface(schema: Schema): schema is SchemaInterfaceExtendingSchema {
  return schema.id != null && APIUtil.isSchemaInterface(schema.id);
}

export function isContainerSchema(schema: Schema): schema is ContainerSchema {
  return schema.id != null && APIUtil.isContainerSchema(schema.id);
}

export function isBoxSchema(schema: Schema): schema is BoxSchema {
  return schema.id != null && APIUtil.isBoxSchema(schema.id);
}

export function isPlateSchema(schema: Schema): schema is PlateSchema {
  return schema.id != null && APIUtil.isPlateSchema(schema.id);
}

export function isLocationSchema(schema: Schema): schema is LocationSchema {
  return schema.id != null && APIUtil.isLocationSchema(schema.id);
}

export function isStorableSchema(schema: Schema | BatchSchema): schema is StorableSchema {
  return schema.id != null && APIUtil.isStorableSchema(schema.id);
}

export function isRequestSchema(schema: Schema | BatchSchema): schema is RequestSchema {
  return (schema as RequestSchema).schemaType === ASSAY_TYPES.ASSAY_REQUEST;
}

export function isResultSchema(schema: Schema | BatchSchema): schema is ResultSchema {
  return (schema as ResultSchema).schemaType === ASSAY_TYPES.ASSAY_RESULT;
}

export function isRunSchema(schema: Schema | BatchSchema): schema is AssayRunSchema {
  return (schema as AssayRunSchema).schemaType === ASSAY_TYPES.ASSAY_RUN;
}

export function isBatchSchema(schema: Schema | BatchSchema | RegistryFilterOption): schema is BatchSchema {
  const batchSchema = schema as BatchSchema;
  return (
    batchSchema.id != null && APIUtil.apiIdToMaybeType(batchSchema.id) === ModelTypeConstants.BATCH_SCHEMA
  );
}

export function isPlateSchemaGraphQL(
  storableSchema?: StorableSchemaGraphQL
): storableSchema is PlateSchemaGraphQL {
  return storableSchema?.__typename === 'PlateSchemaNode';
}

export function isBoxSchemaGraphQL(
  storableSchema?: StorableSchemaGraphQL
): storableSchema is BoxSchemaGraphQL {
  return storableSchema?.__typename === 'BoxSchemaNode';
}

export function isBatchOrTagSchema(
  schema: BatchSchema | TagSchema | RegistryFilterOption
): schema is BatchSchema | TagSchema {
  return isBatchSchema(schema) || isTagSchema(schema);
}
